import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Download, FileSpreadsheet, TrendingUp, Package, Factory, Warehouse } from "lucide-react";
import api from "../utils/api";
import { formatMXN, formatDate } from "../utils/format";
import * as XLSX from "xlsx";

const COLORS = ["#059669", "#0ea5e9", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const TYPE_LABELS = { cotizacion: "Cotización", pedido: "Pedido", factura: "Factura", ticket: "Ticket", importada: "Importada" };

export default function Reportes() {
  const [period, setPeriod] = useState("month");
  const [salesSummary, setSalesSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [productionHistory, setProductionHistory] = useState([]);
  const [inventoryValue, setInventoryValue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ss, tp, ph, iv] = await Promise.all([
        api.get(`/reports/sales-summary?period=${period}`),
        api.get("/reports/top-products"),
        api.get("/reports/production-history"),
        api.get("/reports/inventory-value"),
      ]);
      setSalesSummary(ss.data);
      setTopProducts(tp.data);
      setProductionHistory(ph.data);
      setInventoryValue(iv.data);
    } catch { }
    finally { setLoading(false); }
  };

  const periodLabel = { week: "Semana", month: "Mes", year: "Año" }[period];

  const exportCSV = (data, filename) => {
    if (!data?.length) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.join(","), ...data.map((row) => keys.map((k) => `"${row[k] ?? ""}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${filename}.csv`; a.click();
  };

  const exportXLSX = (data, filename, sheetName = "Datos") => {
    if (!data?.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const exportSalesReport = () => {
    if (!salesSummary?.sales?.length) return;
    const rows = salesSummary.sales.map((s) => ({
      Folio: s.folio || "",
      Tipo: TYPE_LABELS[s.type] || s.type,
      Cliente: s.customer_name || "",
      Total: s.total || 0,
      Estado: s.status || "",
      Fecha: formatDate(s.created_at),
    }));
    exportXLSX(rows, `ventas-${period}`, "Ventas");
  };

  const exportTopProducts = () => {
    if (!topProducts?.length) return;
    const rows = topProducts.map((p, i) => ({
      Posicion: i + 1,
      Producto: p.name,
      "Cantidad vendida (kg)": p.total_qty,
      "Ingreso total": p.total_revenue,
    }));
    exportXLSX(rows, "top-productos", "Top Productos");
  };

  const exportProduction = () => {
    if (!productionHistory?.length) return;
    const rows = productionHistory.map((o) => ({
      Folio: o.folio,
      Producto: o.product_name,
      Lote: o.lot_number || "",
      "Entrada (kg)": o.raw_input_kg,
      "Salida real (kg)": o.actual_output_kg,
      "Rendimiento (%)": o.yield_percentage,
      "Costo/kg": o.cost_per_kg,
      Fecha: formatDate(o.created_at),
    }));
    exportXLSX(rows, "produccion", "Producción");
  };

  const exportInventory = () => {
    if (!inventoryValue?.products?.length) return;
    const rows = inventoryValue.products.map((p) => ({
      SKU: p.sku || "",
      Nombre: p.name,
      Categoría: p.category,
      Stock: p.stock,
      Unidad: p.unit,
      "Stock mínimo": p.min_stock || 0,
      Costo: p.cost || 0,
      "Valor total": (p.stock || 0) * (p.cost || 0),
    }));
    exportXLSX(rows, "inventario", "Inventario");
  };

  return (
    <div className="space-y-6" data-testid="reportes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reportes</h1>
          <p className="text-slate-500 text-sm mt-1">Análisis y estadísticas del negocio</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {["week", "month", "year"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  period === p ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}>
                {{ week: "Semana", month: "Mes", year: "Año" }[p]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Ventas ({periodLabel})</div>
              <div className="text-3xl font-bold text-slate-900">{formatMXN(salesSummary?.total)}</div>
              <div className="text-xs text-slate-500 mt-1">{salesSummary?.count || 0} documentos</div>
            </div>
            <div className="bg-emerald-50 p-2.5 rounded-lg"><TrendingUp size={20} className="text-emerald-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Valor de inventario</div>
              <div className="text-3xl font-bold text-slate-900">{formatMXN(inventoryValue?.total_value)}</div>
              <div className="text-xs text-slate-500 mt-1">{inventoryValue?.products?.length || 0} productos</div>
            </div>
            <div className="bg-blue-50 p-2.5 rounded-lg"><Warehouse size={20} className="text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Producción completada</div>
              <div className="text-3xl font-bold text-slate-900">{productionHistory.length}</div>
              <div className="text-xs text-slate-500 mt-1">órdenes históricas</div>
            </div>
            <div className="bg-violet-50 p-2.5 rounded-lg"><Factory size={20} className="text-violet-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Productos distintos</div>
              <div className="text-3xl font-bold text-slate-900">{topProducts.length}</div>
              <div className="text-xs text-slate-500 mt-1">con ventas registradas</div>
            </div>
            <div className="bg-amber-50 p-2.5 rounded-lg"><Package size={20} className="text-amber-600" /></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Productos más vendidos</h3>
            <div className="flex gap-2">
              <button onClick={() => exportCSV(topProducts, "top-productos")}
                data-testid="export-top-csv"
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 px-2 py-1 rounded-md transition-colors">
                <Download size={12} /> CSV
              </button>
              <button onClick={exportTopProducts}
                data-testid="export-top-xlsx"
                className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 border border-brand-200 px-2 py-1 rounded-md transition-colors">
                <FileSpreadsheet size={12} /> Excel
              </button>
            </div>
          </div>
          {topProducts.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Sin datos de ventas</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topProducts.slice(0, 8)} barSize={24} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={90} />
                <Tooltip formatter={(v) => [formatMXN(v), "Ventas"]}
                  contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="total_revenue" fill="#059669" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Sales by type */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Ventas por tipo de documento</h3>
            <button onClick={exportSalesReport}
              data-testid="export-sales-xlsx"
              className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 border border-brand-200 px-2 py-1 rounded-md transition-colors">
              <FileSpreadsheet size={12} /> Excel ({periodLabel})
            </button>
          </div>
          {!salesSummary?.by_type || Object.keys(salesSummary.by_type).length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Sin ventas en el período</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={Object.entries(salesSummary?.by_type || {}).map(([k, v]) => ({ name: k, value: v }))}
                  cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                  {Object.keys(salesSummary?.by_type || {}).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatMXN(v)}
                  contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                <Legend iconSize={10} iconType="circle" formatter={(v) => TYPE_LABELS[v] || v} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Production History */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Historial de producción</h3>
          <div className="flex gap-2">
            <button onClick={() => exportCSV(productionHistory, "historial-produccion")}
              data-testid="export-prod-csv"
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 px-2 py-1 rounded-md transition-colors">
              <Download size={12} /> CSV
            </button>
            <button onClick={exportProduction}
              data-testid="export-prod-xlsx"
              className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 border border-brand-200 px-2 py-1 rounded-md transition-colors">
              <FileSpreadsheet size={12} /> Excel
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Folio", "Producto", "Lote", "Entrada", "Salida Real", "Rendimiento", "Costo/kg", "Fecha"].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {productionHistory.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">Sin historial</td></tr>
              ) : productionHistory.slice(0, 20).map((o) => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{o.folio}</td>
                  <td className="px-4 py-2.5 font-medium">{o.product_name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{o.lot_number || "-"}</td>
                  <td className="px-4 py-2.5">{o.raw_input_kg} kg</td>
                  <td className="px-4 py-2.5">{o.actual_output_kg} kg</td>
                  <td className="px-4 py-2.5">
                    <span className={o.yield_percentage >= 25 ? "text-brand-600 font-medium" : "text-amber-600 font-medium"}>
                      {o.yield_percentage}%
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-medium">{formatMXN(o.cost_per_kg)}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{formatDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inventory export */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900">Exportar inventario completo</h3>
            <p className="text-xs text-slate-500 mt-1">Descarga todos los productos con su stock y valor actual</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => exportCSV(inventoryValue?.products || [], "inventario")}
              data-testid="export-inv-csv"
              className="flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-lg transition-colors">
              <Download size={14} /> Exportar CSV
            </button>
            <button onClick={exportInventory}
              data-testid="export-inv-xlsx"
              className="flex items-center gap-1.5 text-sm text-white bg-brand-600 hover:bg-brand-700 px-3 py-2 rounded-lg transition-colors">
              <FileSpreadsheet size={14} /> Exportar Excel
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-slate-900">{inventoryValue?.products?.length || 0}</div>
            <div className="text-xs text-slate-500">productos totales</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-emerald-700">{formatMXN(inventoryValue?.total_value)}</div>
            <div className="text-xs text-slate-500">valor total</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-amber-700">{inventoryValue?.low_stock?.length || 0}</div>
            <div className="text-xs text-slate-500">con stock bajo</div>
          </div>
        </div>
      </div>

      {/* Low Stock */}
      {inventoryValue?.low_stock?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Productos con stock bajo</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {inventoryValue.low_stock.map((p) => (
              <div key={p.id} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="font-medium text-slate-800 text-sm">{p.name}</div>
                <div className="text-amber-700 text-xs mt-1">{p.stock} {p.unit} disponible</div>
                <div className="text-slate-500 text-xs">Mín: {p.min_stock} {p.unit}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
